import React, { useState, useCallback, useMemo } from "react";
import {
  Grid,
  Typography,
  Container,
  Modal,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import SearchIcon from "@mui/icons-material/Search";

import AutoSizer from "react-virtualized-auto-sizer";
import StickyList, { innerElementType } from "../components/StickyList";
import AllRows from "../components/AllRows";
import DefaultUPCModal from "../components/DefaultUPCModal";
import CustomModal from "../components/CustomModal";
import ItemDialog from "../../../components/Dialogs/Items/Items";

import useOrderlistData from "../hooks/useOrderlistData";
import useProductFilter from "../hooks/useProductFilter";
import useModalActions from "../hooks/useModalActions";

import { initialStateSelectedItem } from "../scripts/constants";

import "../css/OrderlistPrototype.css";

const OrderlistPrototype = () => {
  const theme = useTheme();
  const xsSize = useMediaQuery(theme.breakpoints.only("xs"));
  const smSize = useMediaQuery(theme.breakpoints.only("sm"));
  const mdSize = useMediaQuery(theme.breakpoints.only("md"));
  const lgSize = useMediaQuery(theme.breakpoints.only("lg"));

  const [openItemDialog, setOpenItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(initialStateSelectedItem);

  const {
    columns,
    products,
    setProducts,
    productsFull,
    orderlists,
  } = useOrderlistData();

  const { query, isMultiSearch, handleQueryChange } = useProductFilter({
    productsFull,
    setProducts,
  });

  const {
    customModal,
    defaultUPCModal,
    orderlistInfo,
    loadingFlag,
    finishedFlag,
    productInfo,
    errorInfo,
    action,
    selectedCategory,
    handleCategoryChange,
    toggleCustomModal,
    toggleDefaultUPCModal,
    dismissCustomModal,
    dismissDefaultUPCModal,
    fetchNDeployCustomModal,
    fetchNDeployDefaultUPCModal,
    sendRequest,
    sendDefaultUPCRequest,
  } = useModalActions({ products, setProducts });

  const displayProductInfo = useCallback(
    (source) => {
      const [, rowId] = source.split("-");
      const productObj = products[rowId];
      setSelectedItem(productObj || initialStateSelectedItem);
      setOpenItemDialog(true);
    },
    [products],
  );

  const handleChange = useCallback(
    (e) => {
      const { checked, name, value } = e.target;
      const [rowId, columnId] = name.split("-");
      const columnData = columns.find((item) => item.id === +columnId);

      if (columnData.exposed) {
        const columnValue = columnData.hasOwnProperty("exposedAs")
          ? columnData.exposedAs === "checkbox"
            ? checked
            : value
          : value;

        const productObj = products[rowId];
        fetchNDeployCustomModal(columnId, productObj, columnValue);
      }
    },
    [columns, products, fetchNDeployCustomModal],
  );

  const handleClickDefaultUPC = useCallback(
    (source) => {
      const [, rowId] = source.split("-");
      const productObj = products[rowId];

      const itemcodeRelated = products
        .filter((item, index) => index !== 0)
        .filter(
          (item) => item.item_code.item_code === productObj.item_code.item_code,
        );

      if (isMultiSearch) {
        const multiSearchRelated = products.filter((item, index) => index !== 0);
        if (multiSearchRelated.length > 1) {
          fetchNDeployDefaultUPCModal(productObj, multiSearchRelated, true);
          return;
        }
      }

      if (itemcodeRelated.length > 1) {
        fetchNDeployDefaultUPCModal(productObj, itemcodeRelated);
      }
    },
    [products, isMultiSearch, fetchNDeployDefaultUPCModal],
  );

  const getItemSize = (index) => {
    const defaultSize = 40;
    if (!products[index + 1]) return defaultSize;
    const last =
      index < products.length
        ? products[index].item_code.item_code !==
          products[index + 1].item_code.item_code
        : false;
    const itemSize = defaultSize + (last ? 3 : 0);
    return index === 0 ? 50 : itemSize;
  };

  const getWidthSize = () => {
    const itemCodeWidth = 100;
    const upcWidth = 150;
    const nameWidth = 200;
    const stockLevelWidth = 80;
    const orderlistWidth = orderlists.length * 70 || 0;
    return itemCodeWidth + upcWidth + nameWidth + stockLevelWidth + orderlistWidth;
  };

  const itemData = useMemo(
    () => ({
      products,
      columns,
      displayProductInfo,
      handleChange,
      handleClickDefaultUPC,
    }),
    [products, columns, displayProductInfo, handleChange, handleClickDefaultUPC],
  );

  return (
    <>
      <Container className="whitetable">
        <Modal open={customModal} onClose={toggleCustomModal}>
          <CustomModal
            loadingFlag={loadingFlag}
            finishedFlag={finishedFlag}
            productInfo={productInfo}
            orderlistInfo={orderlistInfo}
            selectedCategory={selectedCategory}
            errorInfo={errorInfo}
            action={action}
            handleCategoryChange={handleCategoryChange}
            dismissCustomModal={dismissCustomModal}
            sendRequest={sendRequest}
            toggleCustomModal={toggleCustomModal}
          />
        </Modal>
        <Modal open={defaultUPCModal} onClose={toggleDefaultUPCModal}>
          <DefaultUPCModal
            loadingFlag={loadingFlag}
            finishedFlag={finishedFlag}
            productInfo={productInfo}
            orderlistInfo={orderlistInfo}
            selectedCategory={selectedCategory}
            errorInfo={errorInfo}
            action={action}
            handleCategoryChange={handleCategoryChange}
            dismissDefaultUPCModal={dismissDefaultUPCModal}
            sendDefaultUPCRequest={sendDefaultUPCRequest}
            toggleCustomModal={toggleCustomModal}
          />
        </Modal>
        <Grid container spacing={3}>
          <Grid item xs={true} sm={true}>
            <Typography variant="h6" component="div">
              Order Guide Management
            </Typography>
          </Grid>
          <Grid item xs={"auto"} sm={"auto"}>
            <IconButton>
              <SearchIcon />
            </IconButton>
            <input
              className="inputPickList inputPickListText search-box"
              name="query"
              value={query}
              placeholder="Search by Item Code"
              onChange={handleQueryChange}
            />
          </Grid>
          <Grid item xs={12}></Grid>
          <Grid item xs={12}>
            {!products.length || !orderlists.length ? (
              <div align="center">Loading information...</div>
            ) : (
              products.length &&
              orderlists.length && (
                <AutoSizer disableHeight>
                  {({ width }) => (
                    <StickyList
                      height={
                        xsSize || smSize ? 350 : mdSize ? 500 : lgSize ? 550 : 600
                      }
                      contentWidth={getWidthSize()}
                      innerElementType={innerElementType}
                      itemCount={products.length}
                      itemSize={getItemSize}
                      width={width}
                      itemData={itemData}
                    >
                      {AllRows}
                    </StickyList>
                  )}
                </AutoSizer>
              )
            )}
          </Grid>
        </Grid>
        <ItemDialog
          data={selectedItem}
          openItemDialog={openItemDialog}
          setOpenItemDialog={setOpenItemDialog}
        />
      </Container>
    </>
  );
};

export default OrderlistPrototype;